import * as React from 'react';
import Link from '@mui/material/Link';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Title from './Title';
import Paper from '@mui/material/Paper';
import axios from "axios";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import Collapse from '@mui/material/Collapse';
import TableContainer from '@mui/material/TableContainer';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useNavigate } from "react-router-dom";
import LinearProgress from '@mui/material/LinearProgress';

function preventDefault(event) {
    event.preventDefault();
}

// Function to convert billing plan to readable format
function formatBillingPlan(billingPlan) {
    if (!billingPlan) return '';

    const mapping = {
        '1/1': 'M/M',
        '12/1': 'R/M',
        '12/12': 'R/R'
    };

    return mapping[billingPlan] || billingPlan;
}

// Function to format date as dd-MM-yyyy HH:mm
function formatDateTime(dateString) {
    if (!dateString) return '';

    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    return `${day}-${month}-${year} ${hours}:${minutes}`;
}

// Function to format date as dd-MM-yyyy
function formatDate(dateString) {
    if (!dateString) return '';

    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();

    return `${day}-${month}-${year}`;
}

function Row(props) {
    const { order } = props;
    const [open, setOpen] = React.useState(false);

    return (
        <React.Fragment>
            <TableRow>
                <TableCell>
                    <IconButton
                        aria-label="expand row"
                        size="small"
                        onClick={() => setOpen(!open)}
                    >
                        {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                </TableCell>
                <TableCell>{order.Id}</TableCell>
                <TableCell>
                    <Box
                        sx={{
                            display: 'inline-block',
                            px: 1,
                            py: 0.5,
                            borderRadius: 1,
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            backgroundColor: order.Status === 'active' ? '#4caf50' : '#f44336',
                            color: 'white',
                        }}
                    >
                        {order.Status}
                    </Box>
                </TableCell>
                <TableCell>{order.CustomerName}</TableCell>
                <TableCell>{order.ProductName}</TableCell>
                <TableCell>{order.ProductGroup}</TableCell>
                <TableCell align="center">{order.Quantity}</TableCell>
                <TableCell align="right">{order.Amount}</TableCell>
                <TableCell align="center">
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {formatBillingPlan(order.BillingPlan)}
                    </Typography>
                </TableCell>
                <TableCell align="right">{formatDate(order.TerminationDate)}</TableCell>
            </TableRow>
            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0, backgroundColor: '#f5f5f5' }} colSpan={10}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box sx={{ margin: 2 }}>
                            <Typography variant="h6" gutterBottom component="div" sx={{ fontWeight: 600, mb: 2 }}>
                                Szczegóły subskrypcji
                            </Typography>
                            <Table size="small" aria-label="details">
                                <TableHead>
                                    <TableRow>
                                        <TableCell><strong>ParentId</strong></TableCell>
                                        <TableCell><strong>CustomerId</strong></TableCell>
                                        <TableCell><strong>ProductId</strong></TableCell>
                                        <TableCell><strong>BillingPlanId</strong></TableCell>
                                        <TableCell><strong>CreatedAt</strong></TableCell>
                                        <TableCell><strong>DBCreatedAt</strong></TableCell>
                                        <TableCell><strong>DBUpdatedAt</strong></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    <TableRow>
                                        <TableCell component="th" scope="row">{order.ParentId}</TableCell>
                                        <TableCell>{order.CustomerId}</TableCell>
                                        <TableCell>{order.ProductId}</TableCell>
                                        <TableCell>{order.BillingPlanId}</TableCell>
                                        <TableCell>{formatDateTime(order.CreatedAt)}</TableCell>
                                        <TableCell>{formatDateTime(order.DBCreatedAt)}</TableCell>
                                        <TableCell>{formatDateTime(order.DBUpdatedAt)}</TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </React.Fragment>
    );
}

export default function Subscriptions() {
    const [orders, setOrders] = React.useState([]);
    const [loading, setLoading] = React.useState(true);

    React.useEffect(() => {
        (async () => {
            await axios
                .get("db/getsubscriptionsdh/")
                .then((response) => {
                    setOrders(response.data);
                })
                .catch((error) => {
                    console.log(error);
                    if (error?.response?.status === 401) {
                        alert("Your access is not allowed.");
                    }
                });
            setLoading(false);
        })();
    }, [setOrders]);

    let navigate = useNavigate();
    const routeChange = () => {
        let path = "/orders";
        navigate(path);
    }

    const renderOrdersTable = (orders) => {
        return (
            <React.Fragment>
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', width: '90%', margin: '20px auto 0' }}>
                    <Title>Subskrypcje</Title>
                    <TableContainer>
                        <Table size="small" aria-label="collapsible table">
                            <TableHead>
                                <TableRow>
                                    <TableCell />
                                    <TableCell>Id</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>CustomerName</TableCell>
                                    <TableCell>ProductName</TableCell>
                                    <TableCell>ProductGroup</TableCell>
                                    <TableCell align="center">Quantity</TableCell>
                                    <TableCell align="right">Cena BCM</TableCell>
                                    <TableCell align="center">Term/Invoice</TableCell>
                                    <TableCell align="right">TerminationDate</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {orders.map((order, index) => (
                                    <Row key={index} order={order} />
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            </React.Fragment>
        );
    }

    let contents = loading
        ? (
            <Box sx={{ width: '100%' }}>
                <LinearProgress />
                <p><em>&nbsp;&nbsp;Loading... trwa ładowanie danych - stay calm:) ...</em></p>
            </Box>
        )
        : renderOrdersTable(orders);

    return (
        <>
            {contents}
        </>
    );
}
